import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../../api';
import { ApiError, type Correio, type CorreioAnexo, type CorreioCarta } from '../../api/types';
import { iconeDaPrata, iconeDoOuro } from '../../assets/items';
import { aoCorreio } from '../../correio/aviso';
import { hora, numero, t } from '../../i18n/idioma';
import { nomeDaPecaDoTraje, nomeDoItem } from '../../i18n/nomes';
import { GumpWindow } from '../GumpWindow';
import { ItemIcon } from '../ItemIcon';
import { SeloFundador } from '../SeloFundador';
import { useModo } from '../hud/useModoTela';
import { CaixaVazia, CartaAberta, CartaFechada, Encomenda, IconeCorreio } from './ArteCorreio';

/**
 * A janela do Correio (techspec-correio §06): a lista à esquerda, a carta aberta à direita, com os anexos e o Receber.
 * No celular é uma coisa de cada vez. O texto da carta é texto puro: linha em branco separa parágrafo.
 */
type Filtro = 'todas' | 'itens' | 'avisos';
type Modal = 'apagar' | 'apagarLidas' | null;

const PACKS = ['', 'peregrino', 'cavaleiro', 'lorde'] as const;
const pendentes = (c: CorreioCarta) => c.anexos.filter(a => a.estado === 'pendente');

export interface CorreioGumpProps {
  shown: boolean;
  /** Abre nesta carta; `nova` abre a nova mais recente (a carta flutuante). */
  abrir?: string | 'nova' | null;
  onClose(): void;
  /** Quantas novas e quantas com anexo: a carta flutuante e o Mais. */
  onResumo(novas: number, pendentes: number): void;
  /** Algum anexo entrou: a mochila e o cofre releem. */
  onRecebeu(texto: string): void;
}

export function CorreioGump({ shown, abrir, onClose, onResumo, onRecebeu }: CorreioGumpProps) {
  const modo = useModo();
  const umaColuna = modo === 'cel' || modo === 'deitado';
  const [dados, setDados] = useState<Correio | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [modal, setModal] = useState<Modal>(null);
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  const [naoCoube, setNaoCoube] = useState<Readonly<Record<string, string>>>({});
  const [piscar, setPiscar] = useState<readonly string[]>([]);
  const corpoRef = useRef<HTMLDivElement>(null);
  const resumoRef = useRef(onResumo);
  resumoRef.current = onResumo;

  const resumir = useCallback((cartas: readonly CorreioCarta[]) => {
    resumoRef.current(cartas.filter(c => !c.lida).length, cartas.filter(c => pendentes(c).length > 0).length);
  }, []);

  const carregar = useCallback(async () => {
    try {
      const d = await api.correio();
      setDados(d);
      setErro(null);
      resumoRef.current(d.novas, d.pendentes);
      return d;
    } catch (e) {
      setErro(e instanceof Error ? e.message : t('correio.erro'));
      return null;
    }
  }, []);

  const trocar = useCallback((nova: CorreioCarta) => {
    setDados(d => {
      if (!d) return d;
      const cartas = d.cartas.map(c => (c.id === nova.id ? nova : c));
      resumir(cartas);
      return { ...d, cartas };
    });
  }, [resumir]);

  const ler = useCallback((c: CorreioCarta) => {
    setSel(c.id);
    setLendo(true);
    setNaoCoube({});
    corpoRef.current?.scrollTo({ top: 0 });
    if (c.lida) return;
    trocar({ ...c, lida: true });
    void api.correioLer(c.id).catch(() => { /* a carta só volta a ser nova na próxima leitura */ });
  }, [trocar]);

  /* ao abrir: relê e escolhe a carta (a nova, a pedida ou, no PC, a primeira) */
  const abrirRef = useRef(abrir);
  abrirRef.current = abrir;
  useEffect(() => {
    if (!shown) {
      setModal(null);
      setLendo(false);
      return;
    }
    let vivo = true;
    void carregar().then(d => {
      if (!vivo || !d) return;
      const pedido = abrirRef.current;
      const alvo = pedido === 'nova' ? d.cartas.find(c => !c.lida) : pedido ? d.cartas.find(c => c.id === pedido) : null;
      if (alvo) ler(alvo);
      else if (!umaColuna) setSel(s => (s && d.cartas.some(c => c.id === s) ? s : d.cartas.find(c => !c.lida)?.id ?? d.cartas[0]?.id ?? null));
    });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, abrir]);

  useEffect(() => aoCorreio(() => { if (shown) void carregar(); }), [shown, carregar]);

  useEffect(() => {
    if (!recado) return;
    const id = window.setTimeout(() => setRecado(null), 3200);
    return () => window.clearTimeout(id);
  }, [recado]);
  useEffect(() => {
    if (!piscar.length) return;
    const id = window.setTimeout(() => setPiscar([]), 700);
    return () => window.clearTimeout(id);
  }, [piscar]);

  const cartas = dados?.cartas ?? [];
  const carta = cartas.find(c => c.id === sel) ?? null;
  const novas = cartas.filter(c => !c.lida).length;
  const comItens = cartas.filter(c => pendentes(c).length > 0).length;
  const lidasLimpas = cartas.filter(c => c.lida && pendentes(c).length === 0).length;
  const lista = useMemo(() => cartas.filter(c =>
    filtro === 'itens' ? c.anexos.length > 0 : filtro === 'avisos' ? c.anexos.length === 0 : true), [cartas, filtro]);

  async function receber(c: CorreioCarta, anexo?: CorreioAnexo) {
    if (ocupado) return;
    setOcupado(true);
    try {
      const r = await api.correioReceber(c.id, anexo?.id);
      trocar(r.carta);
      const nao: Record<string, string> = {};
      for (const n of r.naoCouberam) nao[n.anexo] = n.motivo;
      setNaoCoube(nao);
      const antes = new Set(pendentes(c).map(a => a.id));
      const foram = r.carta.anexos.filter(a => antes.has(a.id) && a.estado === 'recebido');
      setPiscar(foram.map(a => a.id));
      if (r.recebidos > 0) {
        const texto = anexo && foram.length === 1
          ? textoRecebido(foram[0])
          : r.naoCouberam.length
            ? t('correio.recebeuParte', { n: r.recebidos, f: r.naoCouberam.length })
            : t('correio.recebeuTudo', { n: r.recebidos });
        setRecado(texto);
        onRecebeu(t('correio.diario', { titulo: c.titulo, n: r.recebidos }));
      } else if (r.naoCouberam.length) {
        setRecado(motivoTexto(r.naoCouberam[0].motivo));
      }
    } catch (e) {
      setRecado(e instanceof Error ? e.message : t('correio.erro'));
      if (e instanceof ApiError && e.status === 404) void carregar();
    } finally {
      setOcupado(false);
    }
  }

  async function confirmarApagar() {
    if (ocupado) return;
    setOcupado(true);
    try {
      if (modal === 'apagar' && carta) {
        await api.correioApagar(carta.id);
        setDados(d => {
          if (!d) return d;
          const resto = d.cartas.filter(x => x.id !== carta.id);
          resumir(resto);
          return { ...d, cartas: resto };
        });
        setSel(null);
        setLendo(false);
        setRecado(t('correio.apagada'));
      } else if (modal === 'apagarLidas') {
        const n = await api.correioApagarLidas();
        setRecado(t('correio.apagadas', { n }));
        const d = await carregar();
        if (d && !d.cartas.some(c => c.id === sel)) { setSel(null); setLendo(false); }
      }
    } catch (e) {
      setRecado(e instanceof Error ? e.message : t('correio.erro'));
    } finally {
      setModal(null);
      setOcupado(false);
    }
  }

  const tituloExtra = (
    <span className="co__novas">
      {novas > 0 ? <><b>{numero(novas)}</b> {t(novas > 1 ? 'correio.novasN' : 'correio.novas1')}</> : t('correio.tudoLido')}
    </span>
  );

  return (
    <GumpWindow
      title={t('correio.titulo')}
      anchor={{ left: 310, top: 92 }}
      className="win--correio"
      posicao="correio"
      shown={shown}
      comeSlots
      headLead={<IconeCorreio className="co__ico-titulo" />}
      tituloExtra={tituloExtra}
      onClose={() => { if (modal) setModal(null); else onClose(); }}
    >
      <div className={`co${lendo ? ' is-lendo' : ''}`}>
        <div className="co-lista">
          <div className="co-filtro" role="group" aria-label={t('correio.filtro')}>
            <button type="button" aria-pressed={filtro === 'todas'} onClick={() => setFiltro('todas')}>
              {t('correio.filtro.todas')}<b>{cartas.length}</b>
            </button>
            <button type="button" aria-pressed={filtro === 'itens'} onClick={() => setFiltro('itens')}>
              {t('correio.filtro.itens')}{comItens > 0 && <b>{comItens}</b>}
            </button>
            <button type="button" aria-pressed={filtro === 'avisos'} onClick={() => setFiltro('avisos')}>
              {t('correio.filtro.avisos')}
            </button>
          </div>
          <div className="co-itens" role="listbox" aria-label={t('correio.cartas')}>
            {!dados && !erro && <p className="co-vazio-l">{t('correio.carregando')}</p>}
            {erro && <p className="co-vazio-l">{erro}</p>}
            {dados && lista.length === 0 && <p className="co-vazio-l">{t('correio.nadaAqui')}</p>}
            {lista.map(c => <LinhaCarta key={c.id} carta={c} sel={c.id === sel} onClick={() => ler(c)} />)}
          </div>
          <div className="co-pe">
            <small>{t('correio.regraLista')}</small>
            <button type="button" className="btn btn--tiny btn--ghost" disabled={!lidasLimpas || ocupado} onClick={() => setModal('apagarLidas')}>
              {t('correio.apagarLidas')}
            </button>
          </div>
        </div>

        {carta ? (
          <div className="co-ler">
            <div className="co-ler__h">
              <button type="button" className="btn btn--tiny btn--ghost co-voltar" onClick={() => setLendo(false)}>‹ {t('correio.cartas')}</button>
              <span className="co-origem">{t(`correio.origem.${carta.origem}`)}</span>
              <span className="co-ler__q">{quando(carta.em)}</span>
              <h3 className="co-ler__t">{carta.titulo}</h3>
              <span className="co-ler__de">{t('correio.de', { quem: carta.remetente })}</span>
            </div>
            <div className="co-ler__b" ref={corpoRef}>
              <div className="co-papel">{paragrafos(carta.texto)}</div>
              {carta.anexos.length > 0 && (
                <>
                  <p className="co-sec">
                    {t('correio.anexos')}
                    <span>{pendentes(carta).length
                      ? t('correio.anexosFaltam', { n: pendentes(carta).length, total: carta.anexos.length })
                      : t('correio.anexosTodos')}</span>
                  </p>
                  <div className="co-anexos">
                    {carta.anexos.map(a => (
                      <Anexo
                        key={a.id}
                        anexo={a}
                        motivo={naoCoube[a.id]}
                        pisca={piscar.includes(a.id)}
                        ocupado={ocupado}
                        onReceber={() => void receber(carta, a)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="co-ler__pe">
              <small>{notaDoPe(carta)}</small>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={pendentes(carta).length > 0 || ocupado}
                title={pendentes(carta).length > 0 ? t('correio.apagarAntes') : undefined}
                onClick={() => setModal('apagar')}
              >
                {t('correio.apagar')}
              </button>
              {carta.anexos.length > 0 && (
                <button type="button" className="btn btn--primary" disabled={!pendentes(carta).length || ocupado} onClick={() => void receber(carta)}>
                  {t('correio.receberTudo')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="co-ler">
            <div className="co-nada">
              <CaixaVazia className="co-nada__arte" />
              <p><b>{cartas.length ? t('correio.escolha') : t('correio.vazia')}</b> {cartas.length ? t('correio.escolhaDesc') : t('correio.vaziaDesc')}</p>
            </div>
          </div>
        )}

        {modal && (
          <div className="co-modal" role="dialog" aria-modal="true" aria-labelledby="co-mt">
            <div className="co-modal__c">
              <h3 id="co-mt">{modal === 'apagar' ? t('correio.apagarTitulo') : t('correio.apagarLidasTitulo', { n: lidasLimpas })}</h3>
              <p>{modal === 'apagar' ? t('correio.apagarTexto', { titulo: carta?.titulo ?? '' }) : t('correio.apagarLidasTexto')}</p>
              <div className="row">
                <button type="button" className="btn btn--ghost" onClick={() => setModal(null)}>{t('comum.cancelar')}</button>
                <button type="button" className="btn btn--danger" disabled={ocupado} onClick={() => void confirmarApagar()}>{t('correio.apagar')}</button>
              </div>
            </div>
          </div>
        )}
        {recado && <div className="co-recado" role="status"><Encomenda className="co-recado__arte" /><span>{recado}</span></div>}
      </div>
    </GumpWindow>
  );
}

function LinhaCarta({ carta, sel, onClick }: { carta: CorreioCarta; sel: boolean; onClick(): void }) {
  const p = pendentes(carta).length;
  const arte = p ? <Encomenda /> : carta.lida ? <CartaAberta /> : <CartaFechada />;
  return (
    <button type="button" className={`co-l${carta.lida ? '' : ' is-nova'}`} role="option" aria-selected={sel} onClick={onClick}>
      <span className="co-l__ico">{arte}</span>
      <span className="co-l__txt">
        <span className="co-l__t">{carta.titulo}</span>
        <span className="co-l__m">{carta.remetente}</span>
      </span>
      <span className="co-l__d">
        {!carta.lida && <i className="co-ponto" aria-label={t('correio.naoLida')} />}
        {quando(carta.em)}
        {carta.anexos.length > 0 && (p
          ? <span className="co-chip">{t(p > 1 ? 'correio.chipN' : 'correio.chip1', { n: p })}</span>
          : <span className="co-chip is-ok">{t('correio.chipOk')}</span>)}
      </span>
    </button>
  );
}

function Anexo({ anexo: a, motivo, pisca, ocupado, onReceber }: {
  anexo: CorreioAnexo; motivo?: string; pisca: boolean; ocupado: boolean; onReceber(): void;
}) {
  const pego = a.estado === 'recebido';
  const cancelado = a.estado === 'cancelado';
  const nao = !pego && !cancelado && !!motivo;
  return (
    <div className={`co-a${pego || cancelado ? ' is-pego' : ''}${nao ? ' is-nao' : ''}${pisca ? ' is-recebendo' : ''}`}>
      <span className="co-a__ico">
        <IconeAnexo anexo={a} />
        {a.qtd > 1 && a.tipo === 'item' && <span className="co-a__q">{numero(a.qtd)}</span>}
      </span>
      <b>{nomeDoAnexo(a)}</b>
      {pego && <small>{t('correio.recebido')}</small>}
      {cancelado && <small>{t('correio.cancelado')}</small>}
      {nao && <small className="co-erro">{motivoTexto(motivo!)}</small>}
      {!pego && !cancelado && (
        <>
          {!nao && <small>{t(`correio.destino.${a.destino}`)}</small>}
          <button type="button" className="btn btn--tiny" disabled={ocupado} onClick={onReceber}>{t('correio.receber')}</button>
        </>
      )}
    </div>
  );
}

function IconeAnexo({ anexo: a }: { anexo: CorreioAnexo }): ReactNode {
  switch (a.tipo) {
    case 'item': return <ItemIcon id={a.def ?? ''} size={48} />;
    case 'ouro': return <img src={iconeDoOuro(a.qtd)} alt="" draggable={false} />;
    case 'prata': return <img src={iconeDaPrata(a.qtd)} alt="" draggable={false} />;
    case 'traje': return <img src={`/sprites/itens/traje/${a.def}.png`} alt="" draggable={false} />;
    case 'selo': return <SeloFundador nivel={Math.max(1, PACKS.indexOf((a.def ?? '') as typeof PACKS[number]))} className="co-a__selo" />;
    case 'vip': return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M3 18h18l-1.6-9-4.4 3.6L12 5l-3 7.6L4.6 9z" fill="#e6c452" stroke="#7a5a12" strokeWidth="1.2" strokeLinejoin="round" />
      </svg>
    );
  }
}

export function nomeDoAnexo(a: CorreioAnexo): string {
  switch (a.tipo) {
    case 'item': return nomeDoItem(a.def ?? '', a.def ?? '');
    case 'ouro': return t('correio.anexo.ouro', { n: numero(a.qtd) });
    case 'prata': return t('correio.anexo.prata', { n: numero(a.qtd) });
    case 'vip': return t('correio.anexo.vip', { n: a.qtd });
    case 'selo': return t('correio.anexo.selo', { pack: t(`correio.selo.${a.def ?? 'peregrino'}`) });
    case 'traje': return nomeDaPecaDoTraje(a.def ?? '', a.def ?? '');
  }
}

function textoRecebido(a: CorreioAnexo): string {
  const nome = nomeDoAnexo(a);
  switch (a.destino) {
    case 'conta': return a.tipo === 'vip' ? t('correio.foi.vip', { n: a.qtd }) : t('correio.foi.selo', { nome });
    case 'guarda': return t('correio.foi.guarda', { nome });
    case 'cofre': return t('correio.foi.cofre', { nome });
    default: return t('correio.foi.mochila', { nome: a.qtd > 1 ? `${nome} ×${numero(a.qtd)}` : nome });
  }
}

function motivoTexto(motivo: string): string {
  return t(['mochila', 'vip', 'cofre'].includes(motivo) ? `correio.motivo.${motivo}` : 'correio.motivo.item');
}

function notaDoPe(c: CorreioCarta): string {
  if (!c.anexos.length) return t('correio.pe.aviso');
  if (!pendentes(c).length) return t('correio.pe.tudo');
  return c.daConta ? t('correio.pe.daConta') : t('correio.pe.espera');
}

/** "agora", "14:32" (hoje), "ontem", "12/10". */
function quando(ms: number): string {
  const d = new Date(ms);
  const agora = new Date();
  if (agora.getTime() - ms < 60_000) return t('correio.agora');
  const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dias = Math.round((dia(agora) - dia(d)) / 86_400_000);
  if (dias <= 0) return hora(ms);
  if (dias === 1) return t('correio.ontem');
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Texto puro em parágrafos: a primeira linha com vírgula é a saudação; o que começa com "—" é a assinatura. */
function paragrafos(texto: string): ReactNode {
  const ps = texto.replace(/\r\n/g, '\n').split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  return ps.map((p, i) => {
    const linhas = p.split('\n');
    const ola = i === 0 && linhas.length === 1 && p.length < 60 && p.endsWith(',');
    const ass = i === ps.length - 1 && i > 0 && linhas.some(l => l.startsWith('—') || l.startsWith('- '));
    return (
      <p key={i} className={ola ? 'co-ola' : ass ? 'co-ass' : undefined}>
        {linhas.map((l, j) => <span key={j}>{j > 0 && <br />}{l}</span>)}
      </p>
    );
  });
}
